import React, { useState, createContext, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

const japaneseTokens = {
    inkBlack:    '#0f1117',
    deepNavy:    '#141927',
    midNavy:     '#1c2540',
    sidebarBg:   '#131926',
    crimson:     '#287bd6',
    crimsonHover:'#3f8fe0',
    washi:       '#f5f3ef',
    parchment:   '#ede9e0',
    cream:       '#fdfcfa',
    gold:        '#c9a84c',
    mist:        '#8a9bb5',
    ink:         '#1e2030',
};

const buildTheme = (mode) =>
    createTheme({
        palette: {
            mode,
            primary: {
                main: japaneseTokens.crimson,
                light: japaneseTokens.crimsonHover,
                dark: '#1f6fca',
                contrastText: '#ffffff',
            },
            secondary: {
                main: japaneseTokens.gold,
                contrastText: '#ffffff',
            },
            error: {
                main: japaneseTokens.crimson,
                light: japaneseTokens.crimsonHover,
                dark: '#1f6fca',
                contrastText: '#ffffff',
            },
            background: {
                default: mode === 'light' ? '#ede8e0' : '#0d1117',
                paper:   mode === 'light' ? japaneseTokens.cream  : '#161b22',
            },
            text: {
                primary:   mode === 'light' ? japaneseTokens.ink  : '#e6edf3',
                secondary: mode === 'light' ? '#5a6275'           : '#8b949e',
            },
            divider: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
        },
        typography: {
            fontFamily: "'Inter', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            h1: { fontWeight: 700, letterSpacing: '-0.02em' },
            h2: { fontWeight: 700, letterSpacing: '-0.01em' },
            h3: { fontWeight: 600 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            subtitle1: { fontWeight: 500, letterSpacing: '0.01em' },
            body1: { lineHeight: 1.7 },
            body2: { lineHeight: 1.6 },
            button: { fontWeight: 600, letterSpacing: '0.04em', textTransform: 'none' },
        },
        shape: { borderRadius: 8 },
        shadows: [
            'none',
            '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
            '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.05)',
            '0 10px 15px rgba(0,0,0,0.07), 0 4px 6px rgba(0,0,0,0.05)',
            '0 20px 25px rgba(0,0,0,0.08), 0 10px 10px rgba(0,0,0,0.04)',
            '0 25px 50px rgba(0,0,0,0.12)',
            ...Array(19).fill('none'),
        ],
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: mode === 'light' ? japaneseTokens.washi : '#0d1117',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        padding: '8px 20px',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        transition: 'all 0.2s ease',
                    },
                    containedPrimary: {
                        background: `linear-gradient(135deg, ${japaneseTokens.crimson} 0%, #1f6fca 100%)`,
                        boxShadow: '0 2px 8px rgba(40,123,214,0.35)',
                        '&:hover': {
                            background: `linear-gradient(135deg, ${japaneseTokens.crimsonHover} 0%, ${japaneseTokens.crimson} 100%)`,
                            boxShadow: '0 4px 16px rgba(40,123,214,0.45)',
                            transform: 'translateY(-1px)',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 6 },
                },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined' },
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 6,
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    },
                },
            },
        },
    });

export function ThemeProviderWrapper({ children }) {
    const [mode, setMode] = useState('light');

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
            },
        }),
        []
    );

    const theme = useMemo(() => buildTheme(mode), [mode]);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}
